import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminOverview from './pages/AdminOverview';
import AdminUsers from './pages/AdminUsers';

interface AdminLayoutProps {
  onBackToPortal: () => void;
}

export type AdminTab = 'overview' | 'users';

export default function AdminLayout({ onBackToPortal }: AdminLayoutProps) {
  const { user, profile, signOut } = useAuth();
  const [currentTab, setCurrentTab] = useState<AdminTab>('overview');
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);

  const navItems = [
    { id: 'overview' as AdminTab, label: 'Command Overview', icon: '📊' },
    { id: 'users' as AdminTab, label: 'User Management', icon: '👥' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'overview':
        return (
          <AdminOverview
            onNavigateTab={(tab) => {
              setCurrentTab(tab);
            }}
            onOpenCreateUserModal={() => {
              setCurrentTab('users');
              setOpenCreateUserModal(true);
            }}
            onBackToPortal={onBackToPortal}
          />
        );
      case 'users':
        return <AdminUsers initialOpenAddModal={openCreateUserModal} />;
      default:
        return <AdminOverview onNavigateTab={setCurrentTab} onBackToPortal={onBackToPortal} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Top Admin Header */}
      <header className="h-16 bg-slate-900 border-b border-slate-800 text-white px-4 sm:px-6 flex items-center justify-between sticky top-0 z-40 shadow-md">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-tr from-amber-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold shadow-md">
            🛡️
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-sm sm:text-base text-white tracking-wide">
                SAI KRISHNA INDUSTRIES
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-400 text-slate-950 uppercase">
                ADMIN CONSOLE
              </span>
            </div>
            <div className="text-[11px] text-slate-400">System Management · /admin</div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Back to main portal button */}
          <button
            onClick={onBackToPortal}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5 transition-colors"
            title="Return to standard Quotations & Sales Portal"
          >
            <span>←</span>
            <span className="hidden sm:inline">Main Operational Portal</span>
            <span className="sm:hidden">Portal</span>
          </button>

          {/* User profile & signout */}
          <div className="hidden md:flex items-center gap-2 pl-3 border-l border-slate-800 text-xs">
            <div className="text-right">
              <div className="font-bold text-slate-200">{profile?.name || user?.email?.split('@')[0]}</div>
              <div className="text-[10px] text-amber-400 font-mono">ADMINISTRATOR</div>
            </div>
            <div className="h-8 w-8 rounded-full bg-slate-800 text-amber-400 font-bold flex items-center justify-center text-xs border border-amber-500/30">
              {profile?.name ? profile.name.slice(0, 2).toUpperCase() : 'AD'}
            </div>
          </div>

          <button
            onClick={handleSignOut}
            className="p-2 text-slate-400 hover:text-rose-400 hover:bg-slate-800 rounded-lg text-sm transition-colors"
            title="Sign Out"
          >
            🚪
          </button>
        </div>
      </header>

      {/* Admin Body */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Admin Left Sidebar */}
        <aside className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-slate-200 flex flex-col justify-between p-3 sm:p-4 shrink-0 shadow-sm">
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-x-visible pb-2 md:pb-0">
            <div className="hidden md:block px-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              Admin Modules
            </div>

            {navItems.map((item) => {
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentTab(item.id);
                    setOpenCreateUserModal(false);
                  }}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap md:whitespace-normal flex-1 md:flex-none ${
                    isActive
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <span className="text-base">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer Notice */}
          <div className="hidden md:block pt-4 border-t border-slate-100 text-center">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-[11px] text-slate-500 space-y-1">
              <div className="font-bold text-slate-700 flex items-center justify-center gap-1">
                <span>🔒</span> Protected Console
              </div>
              <p className="text-[10px] leading-tight text-slate-400">
                Staff management console for authorized administrators.
              </p>
            </div>
          </div>
        </aside>

        {/* Admin Content Area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}
